import { Alert, Button, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { LatLngBoundsLiteral, Map as LeafletMap } from "leaflet";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import type { APIError } from "src/types";
import { useDrawPolygon } from "src/hooks/useDrawPolygon";
import { useEffect } from "react";
import { featureToWKT } from "src/utils/geometry.utils";
import type { Feature } from "geojson";
import { bbox } from "@turf/bbox";
interface Props {
  map: LeafletMap;
  isLoading: boolean;
  errors?: APIError[] | null;
  onCancel: () => void;
  initialPolygon: Feature | null;
  warningList: string[];
  onSubmit: (polygon_frontendSrid: string) => void;
  clearPolygon: () => void;
  clearWarningList: () => void;
}

export function CaseCreatePredefinedForm(props: Props) {
  const { clearPolygon, clearWarningList } = props;
  const { t } = useTranslation();
  const {
    polygon,
    startDrawing,
    redrawDrawing: deleteDrawing,
    editDrawing,
    stopDrawing,
    renderInitialPolygon,
  } = useDrawPolygon(props.map);

  useEffect(() => {
    if (props.initialPolygon && !polygon) {
      renderInitialPolygon(props.initialPolygon);
      const bounds = bbox(props.initialPolygon);
      const boundsLatLng: LatLngBoundsLiteral = [
        [bounds[1], bounds[0]],
        [bounds[3], bounds[2]],
      ];
      props.map.fitBounds(boundsLatLng, {
        padding: [100, 100],
      });
      clearPolygon();
    }
  }, [props.initialPolygon, polygon, renderInitialPolygon, props.map, clearPolygon]);

  useEffect(() => {
    if (!polygon && !props.initialPolygon) {
      startDrawing();
    } else {
      stopDrawing();
    }
  }, [props.initialPolygon, polygon, startDrawing, stopDrawing]);

  function handleSubmit() {
    if (!polygon) return;
    const polygon_frontendSrid = featureToWKT(polygon);
    props.onSubmit(polygon_frontendSrid);
  }
  return render();

  function render() {
    return (
      <Styles>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h4" color="primary">
              {t("title.createCase")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography>{t("label.drawRegionOnTheMap")}</Typography>
          </Grid>
          {polygon && (
            <Grid item xs={12}>
              <Button
                color="primary"
                onClick={() => {
                  deleteDrawing();
                  clearWarningList();
                }}
              >
                {t("label.redraw")}
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  editDrawing();
                  clearWarningList();
                }}
              >
                {t("label.edit")}
              </Button>
            </Grid>
          )}
          {props.warningList.length > 0 && (
            <Grid item xs={12} className="warning-list">
              {props.warningList.map((warning) => (
                <Alert severity="warning" key={warning}>
                  {warning}
                </Alert>
              ))}
            </Grid>
          )}
          {props.errors && (
            <Grid item xs={12}>
              <ServerErrors data={props.errors} />
            </Grid>
          )}

          <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
            <Button variant="text" onClick={props.onCancel} disabled={props.isLoading}>
              {t("label.previous")}
            </Button>
            <ButtonLoading
              color="primary"
              variant="contained"
              disabled={!polygon}
              onClick={handleSubmit}
              isLoading={props.isLoading}
            >
              {t("label.finish")}
            </ButtonLoading>
          </ButtonGrid>
        </Grid>
      </Styles>
    );
  }
}

const Styles = styled.div`
  .warning-list {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(1)};
  }

  .error-item {
    width: 100%;
  }
`;
